import React from 'react';
import { Box, Typography, Avatar, Tooltip } from '@mui/material';
import {
  DashboardRounded, PeopleAltRounded, BusinessRounded, VerifiedRounded,
  EventRounded, AutoAwesomeRounded, CampaignRounded, StarsRounded,
  AnalyticsRounded, SupportAgentRounded, SettingsRounded, NotificationsRounded,
  VideoSettingsRounded, LogoutRounded, BadgeOutlined, Handshake, VideoCall, Subscriptions, PersonAdd, ReceiptLong, Restore,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import PlatformLogo from '../PlatformLogo';
import './Sidebar.css';

const sections = [
  { label: 'Overview', items: [
    ['/dashboard','Dashboard',DashboardRounded,'#2F6FED'],
    ['/notifications','Notifications',NotificationsRounded,'#8B4DE8'],
  ]},
  { label: 'Workforce', items: [
    ['/employees','Employees',BadgeOutlined,'#2F6FED'],
    ['/crm','CRM',Handshake,'#8B4DE8'],
    ['/employee-meetings','Employee Meetings',VideoCall,'#0F9E9A'],
  ]},
  { label: 'Directory', items: [
    ['/users','Users',PeopleAltRounded,'#13A773'],
    ['/companies','Companies',BusinessRounded,'#F39A24'],
    ['/companies?filter=pending','Verification',VerifiedRounded,'#0F9E9A'],
    ['/registrations/incomplete','Incomplete Registrations',PersonAdd,'#8B4DE8'],
  ]},
  { label: 'Network', items: [
    ['/meetings','Meetings',EventRounded,'#0F9E9A'],
    ['/matchmaking','AI Matchmaking',AutoAwesomeRounded,'#8B4DE8'],
  ]},
  { label: 'Commercial', items: [
    ['/subscription-plans','Subscription Plans',Subscriptions,'#5B58D6'],
    ['/payments/manual','Payment Verification',ReceiptLong,'#13A773'],
    ['/ads','Advertisements',CampaignRounded,'#E84B7A'],
    ['/sponsorships','Sponsorships',StarsRounded,'#F39A24'],
  ]},
  { label: 'Intelligence', items: [
    ['/analytics','Analytics',AnalyticsRounded,'#2F6FED'],
  ]},
  { label: 'System', items: [
    ['/video-settings','Video',VideoSettingsRounded,'#5B58D6'],
    ['/admin/support','Support',SupportAgentRounded,'#13A773'],
    ['/audit-log','Audit Log',Restore,'#8B4DE8'],
    ['/settings','Settings',SettingsRounded,'#62758B'],
  ]},
];

const Sidebar = ({ open = false, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, logout } = useAuth();
  const current = `${location.pathname}${location.search}`;
  const activeFor = (path) => path.includes('?') ? current.startsWith(path) : (location.pathname === path || (path !== '/dashboard' && location.pathname.startsWith(`${path}/`)));
  const initials = String(userData?.displayName || userData?.email || 'Admin').split(/\s+/).slice(0,2).map((p)=>p[0]||'').join('').toUpperCase();

  const go = (path) => {
    navigate(path);
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches) onClose?.();
  };

  return (
    <>
    <button type="button" aria-label="Close navigation" className={`tf-admin-sidebar-backdrop ${open ? 'open' : ''}`} onClick={onClose} />
    <aside className={`tf-admin-sidebar ${open ? 'open' : ''}`}>
      <div className="tf-admin-brand-card" onClick={() => go('/dashboard')} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); go('/dashboard'); } }} role="button" tabIndex={0}>
        <PlatformLogo size={54} radius={14} />
        <div className="tf-admin-brand-copy"><strong>TELCOFAIR</strong><span>PLATFORM ADMINISTRATION</span></div>
      </div>
      <div className="tf-admin-role-chip">PLATFORM WORKSPACE <span>{String(userData?.role || 'admin').replace('_',' ')}</span></div>
      <nav className="tf-admin-nav">
        {sections.map((section) => (
          <div className="tf-admin-nav-section" key={section.label}>
            <div className="tf-admin-section-label"><span>{section.label}</span><i /></div>
            {section.items.map(([path,label,Icon,color]) => {
              const active = activeFor(path);
              return (
                <button key={path} type="button" className={`tf-admin-nav-item ${active?'active':''}`} onClick={() => go(path)}>
                  <span className="tf-admin-nav-icon" style={{ '--item-color': color }}><Icon /></span>
                  <span className="tf-admin-nav-text">{label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="tf-admin-sidebar-bottom">
        <div className="tf-admin-profile-card">
          <Avatar src={userData?.profilePicture || userData?.photoURL || ''}>{initials || 'A'}</Avatar>
          <div><strong>{userData?.displayName || 'Administrator'}</strong><span>{userData?.email || String(userData?.role || 'admin').replace('_',' ')}</span></div>
          <Tooltip title="Sign out"><button type="button" className="tf-admin-logout" onClick={logout}><LogoutRounded /></button></Tooltip>
        </div>
        <button type="button" className="tf-admin-sidebar-logo-footer" onClick={() => go('/dashboard')} aria-label="TELCOFAIR dashboard">
          <PlatformLogo width={204} height={54} radius={10} fit="contain" padding="3% 5%" sx={{ width: '100%', maxWidth: 204, height: 54, borderRadius: '10px', boxShadow: 'none' }} />
        </button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;